import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock, Eye, Handshake, ArrowRight } from "lucide-react";
import LegalShell from "../components/marketing/LegalShell";
import { fadeUp } from "../components/marketing/Blocks";

const CARDS = [
  { icon: Lock, title: "Secure", desc: "We take reasonable measures to protect information.", accent: "text-shipany-cyan", bg: "bg-shipany-cyan/15" },
  { icon: Eye, title: "Transparent", desc: "We explain what information we collect and why.", accent: "text-[#FF9933]", bg: "bg-[#FF9933]/15" },
  { icon: Handshake, title: "Responsible", desc: "We use information only for legitimate business purposes.", accent: "text-[#10B981]", bg: "bg-[#10B981]/15" },
];

const SECTIONS = [
  { id: "collect", h: "Information We Collect", body: ["We collect information needed to operate the ShipAny platform and provide shipping services:"],
    list: ["Name", "Phone number", "Email address", "Business details", "Pickup information", "Delivery information", "Shipment details", "Payment/transaction information", "Device and technical information", "Support communications"] },
  { id: "use", h: "How We Use Information", body: ["Information is used only for legitimate business purposes, including:"],
    list: ["Process shipments", "Provide tracking", "Process transactions", "Provide customer support", "Improve the platform", "Prevent fraud and abuse", "Maintain platform security", "Communicate important service updates"] },
  { id: "sharing", h: "When We Share Information", body: [
    "We share information with relevant service providers and logistics/courier partners where necessary to provide our services — for example, delivery addresses and contact details are shared with the courier performing carriage.",
    "We may disclose information where required by law, regulation or valid legal process. We do not sell personal data.",
  ]},
  { id: "security", h: "Data Security", body: [
    "We apply reasonable technical and organizational measures to protect information, including encrypted transport, access controls and server-side storage of credentials and provider secrets. No method of transmission or storage is completely secure, but we work to protect your information.",
  ]},
  { id: "cookies", h: "Cookies & Tracking Technologies", body: [
    "ShipAny uses strictly necessary cookies for authentication sessions and platform security. We do not use advertising trackers. You can manage cookies in your browser settings; the dashboard requires session cookies to function.",
  ]},
  { id: "retention", h: "Data Retention", body: [
    "We retain account, shipment and transaction information for as long as needed to provide services, meet legal and accounting obligations, resolve disputes and enforce agreements.",
  ]},
  { id: "rights", h: "Your Rights", body: [
    "You may request access to, correction of, or deletion of your personal information, subject to legal and operational requirements. Contact us using the details below to exercise these rights.",
  ]},
  { id: "third-party", h: "Third-Party Services", body: [
    "ShipAny integrates with third-party services such as courier partners, payment processors and authentication providers. Their own privacy policies apply to information they process.",
  ]},
  { id: "children", h: "Children's Privacy", body: [
    "ShipAny is a business platform and is not directed at children. We do not knowingly collect personal information from children.",
  ]},
  { id: "changes", h: "Changes to This Policy", body: [
    "We may update this Privacy Policy from time to time. Material changes will be reflected on this page with an updated date.",
  ]},
  { id: "contact", h: "Contact Us", body: [
    "For privacy-related questions or requests, contact us at shipanylogistics@gmail.com or via the Contact page on ShipAny.in.",
  ]},
];

const TOC = SECTIONS.map((s) => [s.h, s.id]);

export default function Privacy() {
  return (
    <LegalShell testId="privacy-page" kicker="Privacy" title="Your Data. Your Trust. Our Responsibility."
      sub="We are committed to handling personal and business information responsibly and transparently."
      updated="June 2026" toc={TOC} sections={SECTIONS}>
      <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="privacy-overview-cards">
        {CARDS.map((c, i) => (
          <motion.div key={c.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`privacy-card-${c.title.toLowerCase()}`}
            className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${c.bg}`}><c.icon className={`h-5 w-5 ${c.accent}`} /></span>
            <h3 className="font-display mt-4 text-sm font-bold text-white">{c.title}</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{c.desc}</p>
          </motion.div>
        ))}
      </div>
      <motion.div {...fadeUp} className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-shipany-cyan/30 bg-shipany-cyan/5 p-6 sm:flex-row sm:items-center"
        data-testid="privacy-contact-card">
        <div>
          <h3 className="font-display text-base font-bold text-white">Privacy-related question?</h3>
          <p className="mt-1 text-xs text-slate-400">Our team will help with data access, correction or deletion requests.</p>
        </div>
        <Link to="/contact" data-testid="privacy-contact-btn"
          className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-6 py-3 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
          Contact our Privacy Team <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </LegalShell>
  );
}
